import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useLocalization } from 'localization';
import { ORDER_CREATION_PACKAGE_TYPE } from 'constants/ui/status';
import CardTitleHeader from 'components/v2/card/CardTitleHeader';
import StackPlusIcon from 'components/v2/icons/StackPlusIcon';
import AutoCompleteInput from 'components/v2/input/AutoCompleteInput';
import { orderMaterialSchema } from 'components/v2/division/form/schema/orderMaterialSchema';
import { useAuth } from 'modules/auth/hooks';
import { CASES_SEARCH_ENDPOINT } from 'constants/server/apiEndPoint';
import { useDebounce } from 'hooks/general/useDebounce';
import { checkBranchAllowedToCreateOrderApi } from 'api/branches/query';
import { LAYOUT_SPACING } from 'constants/ui/style';
import { useCurrentBranch } from 'hooks/services/useCurrentBranch';
import { useStartCreateNewOrder } from 'hooks/services/useStartCreateNewOrder';

const cardHeaderTitle = {
  [ORDER_CREATION_PACKAGE_TYPE.property]: 'orders.pages.new.flowSelection.orderPhotography',
  [ORDER_CREATION_PACKAGE_TYPE.realtor]: 'orders.pages.new.flowSelection.orderPortrait',
  [ORDER_CREATION_PACKAGE_TYPE.office]: 'orders.pages.new.flowSelection.orderShopPhotos',
  [ORDER_CREATION_PACKAGE_TYPE.ads]: 'Ads', //not defined yet
};

const casePlaceholder = {
  [ORDER_CREATION_PACKAGE_TYPE.property]: 'common.placeholders.caseReference',
  [ORDER_CREATION_PACKAGE_TYPE.realtor]: 'common.placeholders.personalEmail',
  [ORDER_CREATION_PACKAGE_TYPE.office]: 'common.placeholders.officeEmail',
  [ORDER_CREATION_PACKAGE_TYPE.ads]: 'Ads', //not defined yet
};

const DashboardOrderMaterial = () => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const [caseType, setCaseType] = useState(ORDER_CREATION_PACKAGE_TYPE.property);
  const { startCreateNewOrder } = useStartCreateNewOrder();

  const { clientStates } = useCurrentBranch();

  const defaultValuesForm = {
    type: ORDER_CREATION_PACKAGE_TYPE.property,
    selectedDepartmentId: {
      label: clientStates?.currentSelectedBranch?.name ?? user?.branches?.[0]?.name ?? '',
      value: clientStates?.currentSelectedBranch?.id ?? user?.branches?.[0]?.id ?? '',
    },
    selectedAddress: { label: '', value: '' },
  };

  const schema = orderMaterialSchema();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesForm,
    values: defaultValuesForm,
    mode: 'onChange',
  });
  const { control, getValues, setValue, watch, handleSubmit } = methods;
  const selectedDepartment = watch('selectedDepartmentId'); //branches
  const debouncedSearchAddress = useDebounce(watch('selectedAddress.label'));

  /*copying from the old source code*/
  const foundBranchId = useMemo(
    () =>
      user?.isInternalUser
        ? user?.branches.find(({ id }) => id === selectedDepartment.value)?.id
        : selectedDepartment.value,
    [user, selectedDepartment.value]
  );
  /*copying from the old source code*/

  //this is to check if the current selected branchId
  //is being blocked of order creation or not
  const { data: isAllowedToCreateOrder, isLoading } = checkBranchAllowedToCreateOrderApi(foundBranchId ?? '');

  const handleChangeCaseType = (_: React.MouseEvent<HTMLElement>, newAlignment: ORDER_CREATION_PACKAGE_TYPE) => {
    setCaseType(newAlignment);
    setValue('type', newAlignment);
  };
  const handleStartOrdering = async () => {
    const values = getValues();
    const isNewCase = values?.selectedAddress?.value === values?.selectedAddress?.label;

    startCreateNewOrder({
      caseId: isNewCase ? undefined : values?.selectedAddress?.value,
      packageType: caseType,
      caseReference: isNewCase ? values?.selectedAddress?.label : undefined,
      branchId: selectedDepartment.value,
    });
  };

  const departmentOptions = useMemo(() => {
    return (
      user?.branches.map(branch => ({
        label: branch.name,
        value: branch.id,
      })) ?? []
    );
  }, [user?.branches.length]);

  const typeToAllowedToSearchCase = [ORDER_CREATION_PACKAGE_TYPE.property];

  return (
    <FormProvider {...methods}>
      <Card
        sx={{
          flexDirection: 'column',
          width: '100%',
          minHeight: {
            xs: '15rem',
          },
        }}
        component="form"
        onSubmit={handleSubmit(handleStartOrdering)}
      >
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Stack gap={2.5}>
            <CardHeader title={<CardTitleHeader icon={<StackPlusIcon />} title={t(cardHeaderTitle[caseType])} />} />
            <ToggleButtonGroup value={caseType} exclusive onChange={handleChangeCaseType} aria-label="text alignment">
              <ToggleButton value={ORDER_CREATION_PACKAGE_TYPE['property']}>{t(`common.terms.property`)}</ToggleButton>
              {/* <ToggleButton value={ORDER_CREATION_PACKAGE_TYPE['ads']}>{t(`cases.textGroups.ADS`)}</ToggleButton> */}
              <ToggleButton value={ORDER_CREATION_PACKAGE_TYPE['realtor']}>{t(`common.terms.realtor`)}</ToggleButton>
              <ToggleButton value={ORDER_CREATION_PACKAGE_TYPE['office']}>{t(`common.terms.office`)}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack gap={LAYOUT_SPACING.FORM.xs}>
            <AutoCompleteInput
              control={control}
              name="selectedDepartmentId"
              placeholder={t(casePlaceholder[caseType])}
              label="common.terms.department"
              onChange={(_, option) => {
                if (option) {
                  setValue('selectedDepartmentId', option);
                } else {
                  setValue('selectedDepartmentId', { label: '', value: '' });
                  setValue('selectedAddress', { label: '', value: '' });
                }
              }}
              options={departmentOptions}
              isLoading={!departmentOptions?.length}
              required
            />
            <AutoCompleteInput
              control={control}
              freeSolo
              required
              disabled={!watch('selectedDepartmentId.value')}
              name="selectedAddress"
              placeholder={t(casePlaceholder[caseType])}
              label="common.terms.caseTitle"
              queryKey={typeToAllowedToSearchCase.includes(caseType) ? 'searchCases' : ''} //currently it's still used the search case api for this field
              endpoint={CASES_SEARCH_ENDPOINT}
              searchValue={debouncedSearchAddress}
              pickedField={{ label: 'reference', value: 'id' }}
              transformLabel={option =>
                `${option.reference} ${option?.address?.addressFull ? ` (${option.address.addressFull})` : ''}`
              }
              payload={{
                branchIds: [watch('selectedDepartmentId.value')],
                mustHaveOrder: false,
                searchKey: debouncedSearchAddress,
                skip: 0,
                take: 20,
                sortDirection: 'asc',
              }}
              onChange={(_, option) =>
                setValue('selectedAddress', option ?? { label: '', value: '' }, { shouldValidate: true })
              }
              onInputChange={(_, inputValue) =>
                setValue('selectedAddress.label', inputValue, { shouldValidate: true, shouldDirty: true })
              }
              customizeLabelDropdown={t('cases.actions.createNew')}
            />
          </Stack>
        </CardContent>
        <CardActions sx={{ mt: 'auto' }}>
          <Button
            fullWidth
            disabled={!!isAllowedToCreateOrder}
            loading={isLoading}
            variant={!!isAllowedToCreateOrder ? 'outlined' : 'contained'}
            type="submit"
            endIcon={<ArrowForwardIcon />}
          >
            {t('orders.pages.new.flowSelection.submit')}
          </Button>
        </CardActions>
      </Card>
    </FormProvider>
  );
};

export default DashboardOrderMaterial;
